"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/PlaidConnectFlow/steps/PlaidManualUploadStep";

import React, { useEffect, useCallback, useRef, useState } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { Track } from "@/src/lib/tracking";
import { ImageLightbox } from "@components/Chat/InlineAttachment";
import type { UploadedAttachmentFile } from "@/src/utils/attachmentUpload/uploadAttachmentFiles";
import type { SenderAttachment } from "@components/Sender/type";
import {
  PLAID_MAX_MANUAL_FILES,
  usePlaidManualUpload,
} from "../hooks/usePlaidManualUpload";
import {
  PlaidUploadedDocumentChip,
  type PlaidUploadItem,
} from "../shared/PlaidUploadedDocumentChip";
import { PlaidDashedUploadButton } from "../shared/PlaidDashedUploadButton";
import type { PlaidUploadEntry, PlaidUploadFrom } from "../types";

const I18n = I18nUtil.createScoped(Translations);

export interface PlaidManualUploadStepProps {
  onConfirm: (attachments: SenderAttachment[]) => void;
  onBack?: () => void;
  onClose?: () => void;
  initialFiles?: UploadedAttachmentFile[];
  isSubmitting?: boolean;
  /** 重叠分析链路（EDG-561）：缺省 = 线上 V3.0.6 上传页，不受影响 */
  entry?: PlaidUploadEntry;
  from?: PlaidUploadFrom;
}

export const PlaidManualUploadStep: React.FC<PlaidManualUploadStepProps> = ({
  onConfirm,
  onBack,
  onClose,
  initialFiles = [],
  isSubmitting = false,
  entry,
  from,
}) => {
  const {
    rawFiles,
    uploadedFiles,
    isUploading,
    hasError,
    fileInputRef,
    openFilePicker,
    handleFileInputChange,
    removeFile,
    retryFailed,
  } = usePlaidManualUpload(initialFiles, { entry, from });
  const isOverlap = entry === "overlap";
  const attribution = {
    ...(entry ? { entry } : {}),
    ...(from ? { from } : {}),
  };

  const [previewing, setPreviewing] = useState<PlaidUploadItem | null>(null);
  const [autoConfirmAfterRetry, setAutoConfirmAfterRetry] = useState(false);
  const confirmedRef = useRef(false);
  const selectedCountRef = useRef(rawFiles.length);
  selectedCountRef.current = rawFiles.length;

  useEffect(() => {
    Track.view("plaid_manual_upload_view", attribution);
    // 仅挂载时曝光一次；entry / from 在步骤生命周期内不变
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 重叠链路：未 Confirm 就离开（返回 / 关闭 / 被卸载）记一次 close，selected_count 取卸载时的选中数
  useEffect(
    () => () => {
      if (isOverlap && !confirmedRef.current) {
        Track.event("plaid_manual_upload_close", {
          ...attribution,
          selected_count: selectedCountRef.current,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOverlap],
  );

  const handleConfirm = useCallback(() => {
    if (uploadedFiles.length === 0 || isUploading || isSubmitting) {
      return;
    }
    confirmedRef.current = true;
    Track.event("plaid_manual_upload_confirm", {
      file_count: uploadedFiles.length,
      ...attribution,
    });
    onConfirm(uploadedFiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedFiles, isUploading, isSubmitting, onConfirm, entry, from]);

  const handleRetry = useCallback(async () => {
    const ok = await retryFailed();
    if (ok) {
      // 重传成功后自动提交；uploadedFiles 要等状态落地，交给下面的 effect
      setAutoConfirmAfterRetry(true);
    }
  }, [retryFailed]);

  useEffect(() => {
    if (
      autoConfirmAfterRetry &&
      !isUploading &&
      !hasError &&
      uploadedFiles.length > 0
    ) {
      setAutoConfirmAfterRetry(false);
      handleConfirm();
    }
  }, [
    autoConfirmAfterRetry,
    isUploading,
    hasError,
    uploadedFiles.length,
    handleConfirm,
  ]);

  const handlePreview = useCallback(
    (item: PlaidUploadItem) => {
      if (!item.previewUrl && !item.url) {
        return;
      }
      Track.event("plaid_manual_upload_preview_open", attribution);
      setPreviewing(item);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entry, from],
  );

  if (isOverlap) {
    return (
      <div className="flex-1 min-h-0 flex flex-col bg-background_solid_secondary text-content_primary md:p-6">
        <input
          type="file"
          ref={fileInputRef}
          multiple
          accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls"
          className="hidden"
          onChange={handleFileInputChange}
          data-testid="plaid-manual-upload-input"
        />

        {/* H5 头部：只有 40 圆形返回钮（无 close） */}
        <div className="flex shrink-0 px-4 pt-3 md:hidden">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              data-openreplay-label="plaidconnectflow_step_back"
              className="button button_icon flex-center size-10 inverse_button shadow-[0px_2px_20px_rgba(0,0,0,0.08)]"
              aria-label={I18n.t("Go back")}
            >
              <Icon name="left-arrow-alt" size={20} />
            </button>
          )}
        </div>

        {/* PC 头部：back + 标题 + close 同行 */}
        <div className="hidden shrink-0 items-center justify-between pb-6 md:flex">
          <button
            type="button"
            onClick={onBack}
            data-openreplay-label="plaidconnectflow_step_back"
            className="flex-center size-6 cursor-pointer text-content_primary"
            aria-label={I18n.t("Go back")}
          >
            <Icon name="left-arrow-alt" size={24} />
          </button>
          <h1 className="headline_regular_m text-content_primary">
            {I18n.t("Upload what you have")}
          </h1>
          <button
            type="button"
            onClick={onClose}
            data-openreplay-label="plaidconnectflow_step_close"
            className="flex-center size-6 cursor-pointer text-content_primary"
            aria-label={I18n.t("Close")}
          >
            <Icon name="close" size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div
            className={cn(
              "flex flex-col gap-6 px-4 pt-2 pb-10 md:p-0",
              rawFiles.length > 0 && "pb-0",
            )}
          >
            <div className="flex flex-col gap-2">
              <h1 className="headline_regular_m text-content_primary md:hidden">
                {I18n.t("Upload what you have")}
              </h1>
              <p className="body_m text-content_secondary">
                {I18n.t(
                  "Send me a screenshot of your positions page. I'll check which of your funds overlap.",
                )}
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <span className="body_medium_s text-content_tertiary">
                {I18n.t("What I can read")}
              </span>
              <div className="rounded-3 bg-background_solid_primary divide-y divide-border_inverse_transparent10">
                <div className="flex items-center gap-3 p-4">
                  <div className="flex-center size-11 shrink-0 rounded-2 bg-background_brand_overlay10">
                    {/* Figma Thumb/phone（109896:82422）画的是山 + 太阳的图片图标，与下方上传文件 chip 的图片类型图标同款 */}
                    <img
                      src="/assets/images/plaid/manual-upload/guide-landscape.svg"
                      alt=""
                      aria-hidden="true"
                      className="h-6 w-6"
                    />
                  </div>
                  <div className="flex flex-col">
                    <p className="body_bold_m text-content_primary">
                      {I18n.t("Holdings screenshot")}
                    </p>
                    <p className="body_s text-content_tertiary">
                      {I18n.t(
                        "Your Positions or Holdings page, tickers and values visible",
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4">
                  <div className="flex-center size-11 shrink-0 rounded-2 bg-background_brand_overlay10">
                    <img
                      src="/assets/images/plaid/manual-upload/guide-pdf.svg"
                      alt=""
                      aria-hidden="true"
                      className="h-6 w-6"
                    />
                  </div>
                  <div className="flex flex-col">
                    <p className="body_bold_m text-content_primary">
                      {I18n.t("Statement PDF")}
                    </p>
                    <p className="body_s text-content_tertiary">
                      {I18n.t("Monthly or quarterly, last 90 days")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4">
                  <div className="flex-center size-11 shrink-0 rounded-2 bg-background_brand_overlay10">
                    <img
                      src="/assets/images/plaid/manual-upload/guide-csv.svg"
                      alt=""
                      aria-hidden="true"
                      className="h-6 w-6"
                    />
                  </div>
                  <div className="flex flex-col">
                    <p className="body_bold_m text-content_primary">
                      {I18n.t("CSV export")}
                    </p>
                    <p className="body_s text-content_tertiary">
                      {I18n.t("Positions or transactions export")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {rawFiles.length > 0 && (
              <div className="flex flex-col gap-3">
                <span className="body_medium_s text-content_tertiary">
                  {I18n.t("Your documents")}
                </span>
                {hasError && (
                  <p
                    className="body_s text-content_accent2_primary"
                    data-testid="plaid-manual-upload-error-hint"
                  >
                    {I18n.t(
                      "That didn't upload. Your files are still here, try again.",
                    )}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  {rawFiles.map((file) => (
                    <PlaidUploadedDocumentChip
                      key={file.id}
                      file={file}
                      variant="overlap"
                      onRemove={() => removeFile(file.id)}
                      onPreview={handlePreview}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-col gap-3 bg-background_solid_secondary px-4 pt-4 pb-6 md:px-0 md:pb-0">
          <PlaidDashedUploadButton
            // or-label: n/a —— label 由 PlaidDashedUploadButton 内部的 data-openreplay-label="plaidconnectflow_upload_pick" 提供
            onClick={openFilePicker}
            disabled={rawFiles.length >= PLAID_MAX_MANUAL_FILES}
            label={I18n.t("Upload documents")}
            variant="empty"
          />
          <button
            type="button"
            disabled={
              isUploading ||
              isSubmitting ||
              (!hasError && uploadedFiles.length === 0)
            }
            onClick={hasError ? handleRetry : handleConfirm}
            data-openreplay-label={
              hasError
                ? "plaidconnectflow_upload_retry"
                : "plaidconnectflow_upload_overlap_confirm"
            }
            className={cn(
              "button",
              "md_button",
              "primary_button",
              "button_m",
              "w-full",
            )}
          >
            {isSubmitting || isUploading ? (
              <Icon name="Spinner" size={18} className="animate-spin mr-2" />
            ) : null}
            {hasError ? I18n.t("Try again") : I18n.t("Confirm")}
          </button>
        </div>

        {previewing && (
          <ImageLightbox
            src={previewing.previewUrl ?? previewing.url}
            alt={previewing.filename}
            onClose={() => setPreviewing(null)}
            hideDownload
            maskOpacity={0.6}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-background_solid_secondary text-content_primary">
      {/* Hidden file input for file selection */}
      <input
        type="file"
        ref={fileInputRef}
        multiple
        accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls"
        className="hidden"
        onChange={handleFileInputChange}
        data-testid="plaid-manual-upload-input"
      />

      {/* Top Nav Header — fixed outside the scroll area */}
      <div className="flex items-center justify-between w-full shrink-0 min-h-[44px] px-4 pt-3 md:px-6 md:pt-4">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            data-openreplay-label="plaidconnectflow_step_back"
            className="size-10 rounded-full bg-background_solid_primary shadow-[0px_2px_10px_rgba(0,0,0,0.08)] flex items-center justify-center text-content_primary hover:opacity-90 active:scale-95 transition-all cursor-pointer"
            aria-label={I18n.t("Go back")}
          >
            <Icon name="left-arrow-alt" size={20} />
          </button>
        ) : (
          <div className="size-10" />
        )}

        {onClose && (
          <button
            type="button"
            onClick={onClose}
            data-openreplay-label="plaidconnectflow_step_close"
            className="size-10 rounded-full bg-background_solid_primary shadow-[0px_2px_10px_rgba(0,0,0,0.08)] flex items-center justify-center text-content_primary hover:opacity-90 active:scale-95 transition-all cursor-pointer"
            aria-label={I18n.t("Close")}
          >
            <Icon name="close" size={20} />
          </button>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-4 pb-3 md:px-6 md:pb-4">
        <div className="flex flex-col w-full max-w-[400px] mx-auto">
          {/* Intro Block */}
          <div className="flex flex-col gap-2 pt-4 pb-6">
            <h1 className="font-['Lexend'] text-2xl font-normal text-content_primary leading-8 tracking-[-0.96px]">
              {I18n.t("Upload what you have")}
            </h1>
            <p className="text-sm text-content_secondary leading-5 font-normal">
              {I18n.t(
                "Drop in whatever you've got — statements, holdings, an advisor's proposal. It stays private, and I'll make sense of it.",
              )}
            </p>
          </div>

          {/* What I Can Read Guide Card */}
          <div className="flex flex-col gap-3 mb-6">
            <span className="text-xs font-medium text-content_tertiary">
              {I18n.t("What I can read")}
            </span>
            {/* 不加 overflow-hidden：APP WebView 对「border+rounded+overflow-hidden 同元素」按方形盒裁剪，
                背景在四角盖掉边框弧段（线不连续）；子行纯静态堆叠无溢出，裁剪无承载功能（web 视觉零变化） */}
            <div className="bg-background_solid_primary rounded-3 border border-border_main flex flex-col shadow-sm">
              {/* Row 1: Statement PDF */}
              <div className="flex items-center gap-3 p-3.5 sm:p-4 border-b border-border_main">
                <div className="size-11 rounded-2 bg-background_brand_overlay10 flex items-center justify-center shrink-0">
                  <img
                    src="/assets/images/plaid/manual-upload/guide-pdf.svg"
                    alt=""
                    aria-hidden="true"
                    className="h-6 w-6"
                  />
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-semibold text-content_primary leading-5">
                    {I18n.t("Statement PDF")}
                  </p>
                  <p className="text-xs text-content_tertiary leading-4 mt-0.5">
                    {I18n.t("Monthly or quarterly, last 90 days")}
                  </p>
                </div>
              </div>

              {/* Row 2: Holdings screenshot */}
              <div className="flex items-center gap-3 p-3.5 sm:p-4 border-b border-border_main">
                <div className="size-11 rounded-2 bg-background_brand_overlay10 flex items-center justify-center shrink-0">
                  <img
                    src="/assets/images/plaid/manual-upload/guide-landscape.svg"
                    alt=""
                    aria-hidden="true"
                    className="h-6 w-6"
                  />
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-semibold text-content_primary leading-5">
                    {I18n.t("Holdings screenshot")}
                  </p>
                  <p className="text-xs text-content_tertiary leading-4 mt-0.5">
                    {I18n.t("From your broker app, positions visible")}
                  </p>
                </div>
              </div>

              {/* Row 3: CSV export */}
              <div className="flex items-center gap-3 p-3.5 sm:p-4">
                <div className="size-11 rounded-2 bg-background_brand_overlay10 flex items-center justify-center shrink-0">
                  <img
                    src="/assets/images/plaid/manual-upload/guide-csv.svg"
                    alt=""
                    aria-hidden="true"
                    className="h-6 w-6"
                  />
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-semibold text-content_primary leading-5">
                    {I18n.t("CSV export")}
                  </p>
                  <p className="text-xs text-content_tertiary leading-4 mt-0.5">
                    {I18n.t("Positions or transactions export")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Your Documents List (when items exist) */}
          {rawFiles.length > 0 && (
            <div className="flex flex-col gap-2.5 mb-5">
              <span className="text-xs font-medium text-content_tertiary">
                {I18n.t("Your documents")}
              </span>
              <div className="flex flex-col gap-2">
                {rawFiles.map((file) => (
                  <PlaidUploadedDocumentChip
                    key={file.id}
                    file={file}
                    onRemove={() => removeFile(file.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Sticky Action Area */}
      <div className="w-full max-w-[400px] mx-auto flex flex-col gap-3 pt-4 pb-5 shrink-0 px-4 md:px-6 md:pb-6">
        <PlaidDashedUploadButton
          onClick={openFilePicker}
          disabled={rawFiles.length >= 10}
          label={I18n.t("Upload documents")}
          variant="empty"
        />

        <button
          type="button"
          disabled={uploadedFiles.length === 0 || isUploading || isSubmitting}
          onClick={handleConfirm}
          data-openreplay-label="plaidconnectflow_upload_confirm"
          className="w-full h-12 md:h-11 rounded-lg bg-background_brand_overlay100 text-content_inverse_primary font-medium text-base hover:opacity-90 active:scale-[0.99] transition-all cursor-pointer flex items-center justify-center shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <Icon name="Spinner" size={18} className="animate-spin mr-2" />
          ) : null}
          {I18n.t("Confirm")}
        </button>
      </div>
    </div>
  );
};
